import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useConfig } from 'config'
import { getShowingProviderLinkRequest } from 'utils/api/showingChoiceServicesRequests'
import BaseError from './errors/BaseError' // Adjust the import path as needed
import { Container } from '../components'
import { getCookieValueByName } from 'utils'
import { initializePendo, switchRegionCognitoConfig } from 'utils/helper/helper'
import BaseWarning from './notifications/BaseWarning'

const RedirectPage: React.FC = () => {
  const location = useLocation()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null)
  const [actAs, setActAs] = useState<boolean>(false)
  const [isSentrilock, setIsSentrilock] = useState<boolean>(false)
  const [showIntentSelection, setShowIntentSelection] = useState<boolean>(false)
  const [urlOptions, setUrlOptions] = useState<{
    singleUrl: string
    itineraryUrl: string
  } | null>(null)
  const [actAsWarningDismissed, setActAsWarningDismissed] =
    useState<boolean>(false)
  const { config, setConfig } = useConfig()

  const formatErrorMessage = (message: string) => {
    return `${message}. Please close this page.`
  }

  useEffect(() => {
    initializePendo()
    if (!config) return

    // Check if acting as another user - moved here to prevent race condition
    const brightmlsSubscriber = getCookieValueByName('brightmlssubscriber')
    const loggedInSubscriber = getCookieValueByName('loggedinsubscriber')
    const isActAs = !!(
      brightmlsSubscriber &&
      loggedInSubscriber &&
      brightmlsSubscriber !== loggedInSubscriber
    )

    setActAs(isActAs)

    const fetchRedirectUrl = async () => {
      const searchParams = new URLSearchParams(location.search)
      const queryParams: Record<string, string> = {}
      searchParams.forEach((value, key) => {
        queryParams[key] = value
      })

      // Validate that the loggedOnAgentId matches the current user
      const loggedOnAgentId = queryParams.loggedOnAgentId
      if (loggedOnAgentId) {
        const brightmlsSubscriber = getCookieValueByName('brightmlssubscriber')
        const loggedInSubscriber = getCookieValueByName('loggedinsubscriber')

        if (
          (brightmlsSubscriber || loggedInSubscriber) &&
          loggedOnAgentId !== brightmlsSubscriber &&
          loggedOnAgentId !== loggedInSubscriber
        ) {
          setErrorMessage(
            formatErrorMessage('This link is not valid for this user'),
          )
          return
        }
      }

      try {
        const response = await getShowingProviderLinkRequest(
          config,
          queryParams,
        )

        // Check if we have both URLs for intent selection
        if (
          response?.requiresIntent === true &&
          typeof response?.singleUrl === 'string' &&
          response.singleUrl.length > 0 &&
          typeof response?.itineraryUrl === 'string' &&
          response.itineraryUrl.length > 0
        ) {
          // Check if either URL is Sentrilock
          const isSentrilockSingle =
            response.singleUrl.toLowerCase().includes('sentrilock') ||
            response.singleUrl.toLowerCase().includes('sentrikey')
          const isSentrilockItinerary =
            response.itineraryUrl.toLowerCase().includes('sentrilock') ||
            response.itineraryUrl.toLowerCase().includes('sentrikey')

          // If acting as another user and going to Sentrilock, set flag
          if (isActAs && (isSentrilockSingle || isSentrilockItinerary)) {
            setIsSentrilock(true)
          }

          // Set URLs for intent selection
          setShowIntentSelection(true)
          setUrlOptions({
            singleUrl: response.singleUrl,
            itineraryUrl: response.itineraryUrl,
          })
          return
        }

        // Handle standard URL response
        if (response?.url) {
          // Check if URL contains Sentrilock
          const isSentrilockUrl =
            response.url.toLowerCase().includes('sentrilock') ||
            response.url.toLowerCase().includes('sentrikey')
          setIsSentrilock(isSentrilockUrl)

          // If acting as another user and going to Sentrilock, show warning instead of redirecting
          if (isActAs && isSentrilockUrl) {
            setRedirectUrl(response.url)
          } else {
            window.location.href = response.url
          }
        } else {
          setErrorMessage(
            formatErrorMessage(
              'An error occurred while fetching the redirect URL',
            ),
          )
        }
      } catch (error: any) {
        let errorMsg = 'An error occurred while fetching the redirect URL'

        if (error.message) {
          try {
            const errorData = JSON.parse(error.message)
            if (errorData.status === 401) {
              await switchRegionCognitoConfig(config, setConfig)
              return
            }

            if (errorData.data && errorData.data.message) {
              errorMsg = errorData.data.message
            } else if (errorData.message) {
              errorMsg = errorData.message
            }

            if (
              errorMsg.includes('Failed to fetch current provider for member')
            ) {
              errorMsg = `The ability to schedule showings using the Showings button is not enabled for this listing. If you are the Listing Agent, please update your <a href="${config.showingChoiceManagementUrl}" target="_blank">Showing Management preferences</a> in Bright to accept showings.`
            }
          } catch (parseError) {
            console.error('Failed to parse error message:', parseError)
          }
        }

        setErrorMessage(formatErrorMessage(errorMsg))
      }
    }

    fetchRedirectUrl()
  }, [location.search, config])

  const handleIntentSelection = (useSingleUrl: boolean) => {
    if (!urlOptions) return

    try {
      const url = useSingleUrl ? urlOptions.singleUrl : urlOptions.itineraryUrl

      if (!url || url.trim() === '') {
        throw new Error('Selected URL is empty or invalid')
      }

      // Check if URL contains Sentrilock
      const isSentrilockUrl =
        url.toLowerCase().includes('sentrilock') ||
        url.toLowerCase().includes('sentrikey')

      if (actAs && isSentrilockUrl && !actAsWarningDismissed) {
        setIsSentrilock(true)
        setRedirectUrl(url)
      } else {
        try {
          window.location.href = url
        } catch (redirectError) {
          // Fallback redirect method
          window.location.assign(url)
        }
      }
    } catch (error) {
      console.error('Error handling intent selection:', error)
      setErrorMessage(
        formatErrorMessage('An error occurred while processing your selection'),
      )
    }
  }

  const handleCloseError = () => {}

  const handleContinue = () => {
    if (redirectUrl) {
      window.location.href = redirectUrl
    } else if (showIntentSelection) {
      // If we have intent selection, mark Act-As warning as dismissed
      setActAsWarningDismissed(true)
    }
  }

  const handleCloseWarning = () => {}

  // Show Act-As warning first if conditions are met
  if (actAs && isSentrilock && !actAsWarningDismissed) {
    return (
      <Container>
        <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
          <BaseWarning
            onClose={handleCloseWarning}
            header="Warning"
            hideCloseButton={true}
            message={
              <>
                <p>
                  You will be navigating to SentriKey Showing Service, where
                  Act-As is not available at this time.
                </p>
                <div style={{ marginTop: '20px' }}>
                  <button
                    onClick={handleContinue}
                    style={{
                      marginRight: '10px',
                      padding: '8px 16px',
                      backgroundColor: '#0066cc',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    Continue
                  </button>
                  <span style={{ marginLeft: '10px' }}>
                    or close this page.
                  </span>
                </div>
              </>
            }
          />
        </div>
      </Container>
    )
  }

  // Show intent selection after Act-As warning is dismissed (or if no Act-As warning needed)
  if (
    showIntentSelection &&
    urlOptions &&
    (!actAs || !isSentrilock || actAsWarningDismissed)
  ) {
    return (
      <Container>
        <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
          <BaseWarning
            onClose={handleCloseWarning}
            header="Choose an Option"
            hideCloseButton={true}
            message={
              <>
                <p>
                  This listing you are attempting to schedule uses an alternate
                  showing provider.
                </p>
                <div style={{ marginTop: '20px' }}>
                  <button
                    onClick={() => handleIntentSelection(true)}
                    style={{
                      marginRight: '10px',
                      padding: '8px 16px',
                      backgroundColor: '#0066cc',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    Continue to schedule single listing
                  </button>
                  <button
                    onClick={() => handleIntentSelection(false)}
                    style={{
                      marginRight: '10px',
                      padding: '8px 16px',
                      backgroundColor: '#ffffff',
                      color: '#0066cc',
                      border: '1px solid #0066cc',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    Schedule a multiple listing tour
                  </button>
                </div>
              </>
            }
          />
        </div>
      </Container>
    )
  }

  if (errorMessage) {
    return (
      <Container>
        <BaseError
          onClose={handleCloseError}
          header="Error"
          message={<span dangerouslySetInnerHTML={{ __html: errorMessage }} />}
        />
      </Container>
    )
  }

  return null // No content to render if there's no error and redirect happens
}

export default RedirectPage
